import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useAnalytics } from "@/hooks/useAnalytics";
import { INSIGHTS_BACKEND_URL } from "@/lib/axios";
import { MutationConfig } from "@/lib/react-query";
import { axiosApi } from "@/Utils";

import { useScenarioBuilderClient } from "../../api/hooks/useScenarioBuilderClient";
import { useScenariosV3Routes } from "../hooks/useScenariosV3Routes";
import { ScenarioTypeEnum } from "../pages/scenario-form/types";
import { ScenarioType } from "../pages/scenario-form/types";
import { duplicateScenarioV2 } from "./v2/duplicateScenario";

export const duplicateScenario = (scenarioId: string): Promise<any> => {
  return axiosApi.post(
    `/api/v2/scenarios-new/${scenarioId}/clone`,
    {},
    {
      baseURL: INSIGHTS_BACKEND_URL,
    }
  );
};

type UseDuplicateScenarioOptions = {
  config?: MutationConfig<typeof duplicateScenario>;
  isScenariosApiEnabled?: boolean;
};

export const useDuplicateScenario = ({
  config,
  isScenariosApiEnabled = false,
}: UseDuplicateScenarioOptions = {}) => {
  const queryClient = useQueryClient();
  const scenarioBuilderClient = useScenarioBuilderClient();

  const duplicateScenarioMutationFn = isScenariosApiEnabled
    ? (scenarioId: string) =>
        duplicateScenarioV2({ scenarioBuilderClient, scenarioUuid: scenarioId })
    : duplicateScenario;

  const { scenarioCreated } = useAnalytics();
  const { navigateToScenarioForm } = useScenariosV3Routes();
  return useMutation({
    ...config,
    onSuccess: async (data) => {
      toast.success(`"${data.name}" duplicated`);
      scenarioCreated({
        scenarioType: ScenarioType.toString(
          data.scenarioType as ScenarioTypeEnum,
          data.scenarioCount || undefined
        ),
        scenarioId: data.uuid,
        scenarioName: data.name,
        isCloned: true,
      });
      navigateToScenarioForm(data.uuid);
      await queryClient.invalidateQueries({ queryKey: ["scenarios-v2"] });
      await queryClient.invalidateQueries({ queryKey: ["scenarios-new"] });
      await queryClient.invalidateQueries({ queryKey: ["is-optimizer-running"] });
    },
    onError: (data) => {
      toast.error(`"${data.name}" failed to duplicate. Please try again.`);
    },
    mutationFn: duplicateScenarioMutationFn,
  });
};
